import { useEffect } from "react";
import { Link, useFetcher } from "react-router";
import { SafeMarkdown } from "#app/components/safe-markdown.tsx";

interface WordCardEmbedProps {
	slug: string;
}

interface WordData {
	word?: {
		term: string;
		slug: string;
		definitions: Array<{
			definitionShona: string;
			translationEn: string;
			definitionEn: string | null;
		}>;
	};
}

export function WordCardEmbed({ slug }: WordCardEmbedProps) {
	const fetcher = useFetcher<WordData>();

	useEffect(() => {
		if (fetcher.state === "idle" && !fetcher.data) {
			void fetcher.load(`/resources/word-preview/${slug}`);
		}
	}, [fetcher, slug]);

	if (fetcher.state === "loading") {
		return (
			<div className="my-6 animate-pulse rounded-lg border border-border bg-muted/30 p-6">
				<div className="h-6 w-32 rounded bg-muted" />
				<div className="mt-3 h-4 w-full rounded bg-muted" />
			</div>
		);
	}

	const word = fetcher.data?.word;
	if (!word) return null;

	const def = word.definitions[0];
	if (!def) return null;

	return (
		<div className="my-6 rounded-lg border border-border bg-card p-6 transition-colors hover:border-blue-300 dark:hover:border-blue-700">
			<Link
				to={`/words/${word.slug}`}
				className="no-underline hover:no-underline"
			>
				<h4
					className="text-h5 text-foreground"
					style={{ fontFamily: "var(--serif-head)" }}
				>
					{def.definitionShona}
				</h4>
				<p className="mt-1 text-body-sm font-semibold text-foreground">
					{def.translationEn}
				</p>
				{def.definitionEn ? (
					<SafeMarkdown
						content={def.definitionEn}
						className="mt-2 text-body-xs text-muted-foreground"
					/>
				) : null}
				<span className="mt-3 inline-block text-body-2xs text-blue-600 dark:text-blue-400">
					View full definition &rarr;
				</span>
			</Link>
		</div>
	);
}
